import React, { useState } from 'react';
import { useAffiliate } from '@/contexts/AffiliateContext';
import { Eye, EyeOff, LogIn, KeyRound, ArrowLeft, Mail, Lock, AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

export default function AffiliateLoginPage() {
  const { login, verify2FA } = useAffiliate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [pendingToken, setPendingToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');

  // Forgot password state
  const [mode, setMode] = useState('login'); // login, forgot, reset, 2fa
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setPendingToken(result.pending_token);
        setMode('2fa');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FA(pendingToken, twoFACode);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/affiliate/auth/forgot-password`, { email });
      setResetMsg('If an account exists, a reset code has been sent to your email.');
      setMode('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/affiliate/auth/reset-password`, {
        email, code: resetCode, new_password: newPassword
      });
      setResetMsg('Password reset successfully. Please log in.');
      setMode('login');
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const subtitle = {
    login: 'Sign in to your affiliate dashboard',
    forgot: 'Enter your email to receive a reset code',
    reset: 'Enter the code sent to your email',
    '2fa': 'Enter the code from your authenticator app',
  };

  return (
    <div className="min-h-dvh flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0f1729 0%, #1a1a3e 50%, #0f1729 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-indigo-400" />
            </div>
            <span className="text-xl font-bold text-white">Eili Affiliate</span>
          </div>
          <p className="text-slate-400 text-sm">{subtitle[mode]}</p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-300 text-sm rounded-xl px-4 py-3 mb-4" data-testid="affiliate-login-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {resetMsg && (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm rounded-xl px-4 py-3 mb-4" data-testid="affiliate-reset-msg">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {resetMsg}
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition"
                    placeholder="your@email.com" required data-testid="affiliate-email-input" autoComplete="new-password" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-10 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition"
                    placeholder="Your password" required data-testid="affiliate-password-input" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
                data-testid="affiliate-login-btn">
                {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <LogIn className="h-4 w-4" />}
                Sign In
              </button>
              <button type="button" onClick={() => { setMode('forgot'); setError(''); setResetMsg(''); }}
                className="w-full text-center text-xs text-slate-400 hover:text-indigo-400 transition mt-2"
                data-testid="affiliate-forgot-password-link">
                Forgot password?
              </button>
            </form>
          )}

          {mode === '2fa' && (
            <form onSubmit={handle2FA} className="space-y-4" autoComplete="off">
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-indigo-400" />
                </div>
              </div>
              <p className="text-center text-sm text-slate-300 mb-2">Two-Factor Authentication</p>
              <p className="text-center text-xs text-slate-500 mb-4">Enter the 6-digit code from your authenticator app, or a backup code.</p>
              <div>
                <input type="text" value={twoFACode} onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-lg text-center tracking-[0.3em] font-mono placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition"
                  placeholder="000000" required data-testid="affiliate-2fa-code-input" autoComplete="one-time-code" autoFocus />
              </div>
              <button type="submit" disabled={loading || twoFACode.length < 6}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
                data-testid="affiliate-2fa-submit-btn">
                {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Verify
              </button>
              <button type="button" onClick={() => { setMode('login'); setPendingToken(null); setTwoFACode(''); setError(''); }}
                className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-indigo-400 transition">
                <ArrowLeft className="h-3 w-3" /> Back to login
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4" autoComplete="off">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition"
                    placeholder="your@email.com" required data-testid="affiliate-reset-email-input" autoComplete="new-password" />
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
                data-testid="affiliate-send-reset-btn">
                {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                Send Reset Code
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); setResetMsg(''); }}
                className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-indigo-400 transition">
                <ArrowLeft className="h-3 w-3" /> Back to login
              </button>
            </form>
          )}

          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4" autoComplete="off">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Reset Code</label>
                <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm text-center tracking-[0.3em] font-mono placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition"
                  placeholder="000000" required data-testid="affiliate-reset-code-input" autoComplete="off" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition"
                  placeholder="Min 8 characters" required minLength={8} data-testid="affiliate-new-password-input" autoComplete="new-password" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
                data-testid="affiliate-reset-submit-btn">
                {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                Reset Password
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); setResetMsg(''); }}
                className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-indigo-400 transition">
                <ArrowLeft className="h-3 w-3" /> Back to login
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">
          Only registered affiliates can sign in. Contact the administrator to get started.
        </p>
      </div>
    </div>
  );
}
